import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import * as Sentry from "@sentry/react";
import { mutate } from "swr";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Box,
  MenuItem,
  DialogActions,
} from "@material-ui/core";
import SelectField from "components/Forms/SelectField";
import ButtonWithLoading from "components/ButtonWithLoading";
import { isBlank } from "utils/string";
import apiRequest from "utils/apiRequestWithErrorCode";
import { freeManualDomains } from "./freeManualDomains";
import ServerSelector from "./DomainConfigForm/ServerSelector";
import useManualDomains from "../ManualDomains/useManualDomains";

import { Domain } from "@dashboard-v3/api";
import useOrganization from "utils/useOrganization";

type DomainName = string;
type State = {
  domain: DomainName;
  asAlias: boolean;
  aliasFor: DomainName;
  loading: boolean;
  server: string;
  showServerInput: boolean;
  serverError: string;
};

export default function ConnectDomainBtn({ domains }: { domains: Domain[] }) {
  const { t } = useTranslation("orgAndDomains");
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [state, change] = useState<State>(reset());
  const { data: manualDomains } = useManualDomains();
  const { organization } = useOrganization();

  if (!manualDomains) return null;

  function close() {
    setDialogOpen(false);
    change(reset());
  }

  async function connectAsDomain() {
    const domainName = state.domain;
    change(state => ({ ...state, loading: true }));

    const isValid = await validateServer();
    if (!isValid) return change(state => ({ ...state, loading: false }));

    try {
      await apiRequest("POST", "/domains", {
        domain: domainName,
        server: state.server,
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { domainName: domainName } });
      enqueueSnackbar(t("common:errors.fetchError"));
      return;
    }

    mutate("/domains");
    mutate("/manual-domains");
    close();
  }

  async function connectAsAlias() {
    change(state => ({ ...state, loading: true }));

    const domain = domains.find(({ domain }) => domain === state.aliasFor);
    const updates: Domain = {
      ...domain,
      aliases: [...domain.aliases, state.domain],
    };
    try {
      await apiRequest("PUT", `/domains/${state.aliasFor}`, updates);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { alias: state.domain, domain: state.aliasFor },
      });

      enqueueSnackbar(t("common:errors.fetchError"));
      return;
    }

    mutate("/domains");
    mutate("/manual-domains");
    close();
  }

  async function validateServer() {
    if (isBlank(state.server)) {
      change(prev => ({
        ...prev,
        serverError: t("error.domainServer.required"),
      }));

      return false;
    }

    try {
      const isValidHostOrIp = await apiRequest<boolean>(
        "GET",
        `/domains/validate-server/${encodeURIComponent(state.server)}`
      );

      if (!isValidHostOrIp) {
        change(prev => ({
          ...prev,
          serverError: t("error.domainServer.invalid"),
        }));

        return false;
      }
    } catch (error) {
      enqueueSnackbar("error.serverValidation", { variant: "error" });
      Sentry.captureException(error, {
        extra: {
          organizationId: organization.id,
          server: state.server,
        },
      });

      return false;
    }

    return true;
  }

  function onServerChange(server: string) {
    change(prev => ({ ...prev, server, serverError: "" }));
  }

  return (
    <>
      <Button
        color="primary"
        disableElevation
        variant="contained"
        onClick={() => setDialogOpen(true)}
      >
        {t("domainList.add")}
      </Button>
      <Dialog open={dialogOpen} onClose={close}>
        <DialogTitle>{t("connectDialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box mt={2} mb={1}>
              <SelectField
                name="manualOption"
                options={freeManualDomains(manualDomains)}
                renderOption={domain => (
                  <MenuItem key={domain.name} value={domain.name}>
                    {domain.name}
                  </MenuItem>
                )}
                label={t("connectDialog.selectLabel")}
                value={state.domain}
                onChange={event => {
                  change(prev => ({
                    ...prev,
                    domain: event.target.value as string,
                  }));
                }}
              />
            </Box>
            {state.asAlias && (
              <Box mt={2} mb={1}>
                <SelectField
                  name="aliasOf"
                  options={domains}
                  renderOption={domain => (
                    <MenuItem key={domain.domain} value={domain.domain}>
                      {domain.domain}
                    </MenuItem>
                  )}
                  label="As an alias of the domain"
                  value={state.aliasFor}
                  onChange={event => {
                    change(prev => ({
                      ...prev,
                      aliasFor: event.target.value as string,
                    }));
                  }}
                />
              </Box>
            )}
          </DialogContentText>
          {state.showServerInput && (
            <ServerSelector
              domainName={state.domain}
              hasError={Boolean(state?.serverError)}
              errorMsg={state?.serverError}
              value={state.server}
              onChange={onServerChange}
            />
          )}
        </DialogContent>
        <DialogActions style={{ paddingBottom: "20px" }}>
          {state.domain && !state.asAlias && !state.showServerInput && (
            <>
              <ButtonWithLoading
                color="primary"
                variant="contained"
                disableElevation
                onClick={() =>
                  change(prev => ({ ...prev, showServerInput: true }))
                }
                loading={state.loading}
              >
                {t("connectDialog.asDomain")}
              </ButtonWithLoading>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={() => change(prev => ({ ...prev, asAlias: true }))}
                disabled={!domains.length}
              >
                {t("connectDialog.asAlias")}
              </Button>
            </>
          )}
          {state.asAlias && !state.showServerInput && (
            <ButtonWithLoading
              color="primary"
              variant="contained"
              disableElevation
              loading={state.loading}
              onClick={connectAsAlias}
            >
              Save the alias
            </ButtonWithLoading>
          )}

          <Button onClick={close}>{t("common:cancel")}</Button>
          {state.showServerInput && (
            <ButtonWithLoading
              color="primary"
              disableElevation
              type="submit"
              variant="contained"
              data-testid="saveEditBtn"
              loading={state.loading}
              onClick={connectAsDomain}
              disabled={Boolean(state.serverError)}
            >
              {t("common:save")}
            </ButtonWithLoading>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

function reset() {
  return {
    domain: null,
    asAlias: false,
    aliasFor: null,
    loading: false,
    showServerInput: false,
    server: "",
  } as State;
}
